import { useSyncExternalStore } from 'react';
import en from './locales/en.js';

/**
 * Lightweight bilingual layer: French strings are the keys, English comes from the dictionary.
 * A missing translation falls back to French, so the interface never shows a bare key.
 */
export type Locale = 'fr' | 'en';
const STORAGE = 'vename.locale';
const listeners = new Set<() => void>();
function detect(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE);
    if (stored === 'fr' || stored === 'en') return stored;
  } catch {
    // Browser storage may be unavailable; the browser language decides.
  }
  return typeof navigator !== 'undefined' && /^en\b/i.test(navigator.language) ? 'en' : 'fr';
}
let current: Locale = detect();
function syncDocumentLocale(locale: Locale) {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = locale;
  document.title =
    locale === 'en'
      ? 'Vename Mail — Your professional email'
      : 'Vename Mail — Votre messagerie professionnelle';
}
syncDocumentLocale(current);

export function getLocale() {
  return current;
}
export function setLocale(locale: Locale) {
  if (locale === current) return;
  current = locale;
  try {
    localStorage.setItem(STORAGE, locale);
  } catch {
    // Persisting the choice is a convenience; the server keeps the preference too.
  }
  syncDocumentLocale(locale);
  for (const l of listeners) l();
}
export function useLocale(): Locale {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
  );
}
/** BCP 47 tag for Intl formatting in the active language. */
export const localeTag = () => (current === 'en' ? 'en-GB' : 'fr-FR');
export function t(fr: string, vars?: Record<string, string | number>): string {
  const base = current === 'en' ? (en[fr] ?? fr) : fr;
  return vars ? base.replace(/\{(\w+)\}/g, (_: string, k: string) => String(vars[k] ?? '')) : base;
}
